import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { api } from '../../api/client.js';
import { outbox } from '../../offline/index.js';
import { db } from '../../offline/db.js';

// The team's order from a Nano Fibre storeroom. The last copy from the
// server is kept, so it shows with no signal; a change made here shows at
// once ("Sending…") and is dropped once the server has it, or shown as
// turned down if the server refused it.
const MY_KEY = 'nu.stock.orders';
const SENT_KEY = 'nu.stock.orders.sent';

const read = (k, fallback) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? fallback; } catch { return fallback; }
};
const write = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* lasts this visit */ }
};

// { entryId, kind: 'order' | 'cancel' | 'collected', order } — the order as
// it will be once the server has the change.
let sent = read(SENT_KEY, null);
const listeners = new Set();
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const setSent = (v) => { sent = v; write(SENT_KEY, v); listeners.forEach((fn) => fn()); };

export const ORDER_STATUS = {
  ordered: { label: 'Ordered', tone: 'wait' },
  picked: { label: 'Ready to collect', tone: 'ok' },
  handed_over: { label: 'Ready to collect', tone: 'ok' },
  collected: { label: 'Collected', tone: 'done' },
  cancelled: { label: 'Cancelled', tone: 'done' },
};
export const isOpen = (o) => Boolean(o && ['ordered', 'picked', 'handed_over'].includes(o.status));

export function useMyOrders() {
  const [data, setData] = useState(() => read(MY_KEY, null));
  const [status, setStatus] = useState('idle');
  const [refused, setRefused] = useState(null);
  const local = useSyncExternalStore(subscribe, () => sent);

  const load = useCallback(async () => {
    if (sent?.entryId) {
      const e = await db.outbox.get(sent.entryId).catch(() => null);
      if (e?.status === 'rejected') {
        setRefused(e.message || 'That wasn’t accepted.');
        await outbox.discard(e.id);
        setSent(null);
      } else if (!e) {
        setSent(null); // reached the server: what it has is the truth now
      }
    }
    setStatus('loading');
    try {
      const d = await api('/stock/my/orders');
      write(MY_KEY, d);
      setData(d);
      setStatus('ok');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
    const off = outbox.subscribe(load);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    // While it's on screen, look again every 30 seconds, so an order picked
    // at the storeroom turns green without leaving the page.
    const every = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 30_000);
    return () => { off(); clearInterval(every); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);

  // The open order, and the closed ones. A change made here shows at once:
  // cancelled or collected, the order moves straight into the history.
  const mine = local?.order || null;
  const order = mine ? (isOpen(mine) ? mine : null) : (data?.order || null);
  const past = data?.history || [];
  const history = mine && !isOpen(mine) ? [mine, ...past.filter((o) => o.id !== mine.id)] : past;
  // Stock used on the team's jobs, not on an order yet (none once one's just been sent from here).
  const used = local ? [] : data?.used || [];
  // Items ordered in packs and used singly, not a pack's worth yet.
  const building = local ? [] : data?.building || [];
  return { data, status, order, history, used, building, sending: Boolean(local), refused, reload: load };
}

async function send(kind, type, payload, order) {
  setSent({ kind, order });
  const entryId = await outbox.enqueue(type, payload);
  setSent({ kind, order, entryId });
}

// Place the team's order, or change the open one.
// used: [{ bookingId, itemId }], the job stock the form filled in, now on this order.
export function placeOrder(me, { id, storeroom, items, qty, note, previous, used = [] }) {
  const lines = items.filter((i) => Number(qty[i.id]) > 0).map((i) => ({ itemId: i.id, sku: i.sku, description: i.description, qty: Number(qty[i.id]), qtyPicked: null }));
  const order = {
    ...(previous || {}),
    id,
    ref: previous?.ref || null,
    status: 'ordered',
    storeroomId: storeroom.id,
    storeroomName: storeroom.name,
    note,
    orderedByName: previous?.orderedByName || me.name,
    orderedAt: previous?.orderedAt || new Date().toISOString(),
    edits: previous ? (previous.edits || 0) + 1 : 0,
    pickedAt: null,
    lines,
    sent: false,
  };
  return send('order', 'stock.order', { id, storeroomId: storeroom.id, note, lines: lines.map((l) => ({ itemId: l.itemId, qty: l.qty })), used }, order);
}

export function cancelOrder(me, order) {
  return send('cancel', 'stock.order.cancel', { id: order.id }, { ...order, status: 'cancelled', cancelledByName: me.name, cancelledAt: new Date().toISOString() });
}

export function confirmCollected(me, order) {
  return send('collected', 'stock.order.collected', { id: order.id }, { ...order, status: 'collected', collectedBy: 'engineer', collectedByName: me.name, collectedAt: new Date().toISOString() });
}
